import { formatMoney } from 'theme';

/**
 * normalizeProductTags
 * 
 * Normalizes product tags to a consistent array format.
 * 
 * This function handles multiple input formats for product tags and returns a clean array.
 * Why: Product tags can come from different sources in different formats
 * 
 * @param {Array|string|null|undefined} productTags - Product tags in various formats:
 *   - null/undefined: Returns empty array
 *   - Array: Returned as-is
 *   - String: Split by comma, trimmed, and filtered for empty values
 * @returns {Array} Normalized array of product tag strings (empty array if input is invalid)
 */
export const normalizeProductTags = productTags => {
  if (!productTags) return []
  if (Array.isArray(productTags)) return productTags
  if (typeof productTags === 'string') {
    return productTags.split(',').map(tag => tag.trim()).filter(Boolean)
  }
  return []
}

/**
 * buildBadgeClass()
 * 
 * Converts a badge label to a CSS-friendly class name.
 * 
 * @param {string} badge - The badge label text (e.g., "New Arrival", "Limited Edition")
 * @returns {string} CSS-friendly class name in lowercase kebab-case
 * @example
 * buildBadgeClass("Limited Edition") // returns "limited-edition"
 */
const buildBadgeClass = badge => badge.toLowerCase().replace(/\s+/g, '-')



// ==========================================================

// ==========================================================

// ==========================================================



/**
 * buildBadgeHtml()
 * 
 * Generates HTML markup for all product card badges.
 * ***MUST*** match logic in product-badges.liquid.
 * 
 * This function aggregates multiple types of badges based on product data and settings.
 * Badges are small visual indicators shown on product cards to highlight sale status, inventory,
 * custom labels, and more. The function supports:
 * - Custom labels from product tags (via __label: or _badge: syntax)
 * - Custom badges from product metafields
 * - Sold out indicator
 * - Discount/on sale badge with savings amount or percentage
 * 
 * Why: Centralizing badge generation ensures consistent rendering across the store and makes it
 * easy to conditionally show/hide badges based on settings.
 * 
 * @param {Object} params - Configuration object
 * @param {Object} params.product - The Shopify product object containing pricing and availability data
 * @param {Array} params.productTags - Normalized array of product tag strings
 * @param {Object} params.settings - Feature flag and configuration object controlling which badges display:
 *   - showCustomLabels (boolean): Enable custom label badges from tags
 *   - showSoldOutBadge (boolean): Show "Sold Out" badge for unavailable products
 *   - showDiscount (boolean): Show discount/sale badge
 *   - discountMode (string): 'percentage' or 'money' format for discount display
 * @param {Object} params.strings - Localized text strings (e.g., soldOutBadge label)
 * @param {boolean} params.isB2B - If true, suppresses all badges (B2B typically doesn't use them)
 * @returns {string} Concatenated HTML string of badge elements (empty string if no badges or B2B mode)
 */
export const buildBadgeHtml = ({
  product,
  productTags,
  settings,
  strings,
  isB2B,
}) => {
  // Suppress all badges for B2B customers
  if (isB2B) return ''

  const badges = []

  // SECTION 1: Custom labels from product tags
  // Looks for tags with __label: or _badge: syntax and extracts the custom text
  // Example tags: "custom__label:Free Shipping" or "custom_badge:Exclusive"
  if (settings.showCustomLabels) {
    for (const rawTag of productTags) {
      const tag = String(rawTag)
      if (!tag) continue

      if (tag.includes('__label') || tag.includes('_badge')) {
        // Extract the label text after the colon
        const labelText = tag.includes('__label')
          ? tag.split('__label:')[1]
          : tag.split('_badge:')[1]

        badges.push(`<span class="badge badge--custom">${labelText}</span>`)
        // Only add one custom label per product (break after first match)
        break
      }
    }
  }

  // SECTION 2: Custom badges from product metafields *************************************** check if this is actually a thing
  // Checks the product's custom.badges metafield for an array of badge texts
  // Why: This allows flexible badge management without using product tags
  const customBadges = product.metafields?.custom?.badges?.value
  if (Array.isArray(customBadges)) {
    for (const rawBadge of customBadges) {
      const badge = String(rawBadge)
      if (!badge) continue
      // Each metafield badge gets its own badge element with a dynamically generated class
      badges.push(
        `<span class="badge badge--custom badge--${buildBadgeClass(badge)}">${badge}</span>`,
      )
    }
  }

  // SECTION 3: Sold out badge
  if (settings.showSoldOutBadge && !product.availableForSale) {
    badges.push(
      `<sold-out-badge class="badge badge--sold-out">${strings.soldOutBadge || 'Sold Out'}</sold-out-badge>`,
    )
  }

  // SECTION 4: Discount/on-sale badge
  // Shows percentage of savings (30% off) or dollar amount ($26 off) depending on settings
  if (
    settings.showDiscount &&
    parseFloat(product.compareAtPriceRange?.minVariantPrice?.amount || 0) >
    parseFloat(product.priceRange?.minVariantPrice?.amount || 0)
  ) {
    const comparePrice = parseFloat(product.compareAtPriceRange.minVariantPrice.amount)
    const price = parseFloat(product.priceRange.minVariantPrice.amount)
    let savings = ''

    // Calculate savings in either percentage or absolute dollar amount based on settings
    if (settings.discountMode === 'percentage') {
      savings = Math.round(((comparePrice - price) / comparePrice) * 100) + '%'
    } else {
      // formatMoney multiplies by 100 to convert to cents, then formats as currency
      savings = formatMoney((comparePrice - price) * 100)
    }

    // Check if the specific variant (not just product range) is on sale
    const variant = product.variants?.[0]
    const isVariantOnSale = variant?.compareAtPrice > variant?.price

    // Determine badge text: show "On Sale" for range sales, or "Save [amount]" for active sales
    const discountText =
      !isVariantOnSale && product.compareAtPriceRange?.minVariantPrice
        ? 'On Sale'
        : `Save ${savings}`

    badges.push(`<on-sale-badge class="badge badge--on-sale">${discountText}</on-sale-badge>`)
  }

  // Return all badges concatenated as a single HTML string

  return badges.join('')
}

// ==========================================================

// ==========================================================

// ==========================================================

/**
 * buildSaveMessageHtml
 * 
 * Generates HTML markup for product card save messages/callout text.
 * Core logic is coming from product-card-renderers.js.
 * 
 * This function creates promotional and informational messages displayed below or alongside product cards.
 * Supports multiple message types triggered by product tags and settings:
 * - Save message (generic promotional text)
 * - Final sale indicator
 * - Exclusion message (items not available on sale)
 * - Bundle discount message
 * - Custom info/text messages embedded in tags
 * 
 * Why: Messages provide additional context to customers (shipping info, return policies, exclusions, etc.)
 * and allow flexible promotional messaging without modifying product descriptions.
 * 
 * @param {Object} params - Configuration object
 * @param {Object} params.product - The Shopify product object
 * @param {Array} params.productTags - Normalized array of product tag strings
 * @param {Object} params.settings - Feature flags and message configuration:
 *   - showSaveMessage (boolean): Show generic save message
 *   - saveMessageTag (string): Product tag that triggers save message
 *   - saveMessageRegion (string): Region restriction ('ALL' or specific region code)
 *   - exclusionMessageTag (string): Tag marking products excluded from promotions
 *   - showExclusionMessage (boolean): Show message for excluded products
 *   - exclusionMessage (string): Text to show for excluded products
 *   - showCustomLabels (boolean): Enable custom messages from tags (_info:, _text:)
 *   - showFinalSaleMessage (boolean): Show final-sale indicator
 *   - finalSaleMessage (string): Final sale message text
 *   - bundleProductDiscountMessage (string): Message for bundle products
 *   - saveMessage (string): Generic save message text
 *   - saveMessageBox (boolean): Add box styling to save message
 * @param {string} params.userRegion - Customer's region for regional promotions
 * @returns {string} Concatenated HTML string of message divs (empty string if no messages or B2B mode)
 */

export const buildSaveMessageHtml = ({
  product,
  productTags,
  settings,
  strings,
  isB2B,
  userRegion,
}) => {

  // Suppress all messages for B2B customers
  if (isB2B) return ''

  const messages = []
  const saveMessageTag = String(settings.saveMessageTag || '').trim()
  const normalizedRegion = String(settings.saveMessageRegion || 'ALL').trim().toUpperCase()
  const normalizedUserRegion = String(userRegion).trim().toUpperCase()

  // SECTION 1: Check if product is excluded from promotions
  // Products with exclusion tag won't show save messages, but will show exclusion message
  let excludeFromSave = false;

  if (settings.exclusionMessageTag && settings.exclusionMessageTag != "") {
    excludeFromSave = productTags.some(tag => String(tag).includes(settings.exclusionMessageTag));
  }

  // SECTION 2: Generic save message
  // Triggered by saveMessageTag, respects regional promotion settings
  // Why: Main promotional message to highlight deals across store
  if (settings.showSaveMessage && !excludeFromSave) {
    if (normalizedRegion === 'ALL' || normalizedRegion === normalizedUserRegion) {
      if (!saveMessageTag) {
        // No tag specified — applies to every non-excluded product
        const boxClass = settings.saveMessageBox ? 'save-message--box' : ''
        messages.push(`<div class="save-message ${boxClass}">${settings.saveMessage}</div>`)
      } else {
        for (const rawTag of productTags) {
          const tag = String(rawTag).trim()
          if (!tag) continue

          if (tag.includes(saveMessageTag)) {
            const boxClass = settings.saveMessageBox ? 'save-message--box' : ''
            messages.push(`<div class="save-message ${boxClass}">${settings.saveMessage}</div>`)
            break
          }
        }
      }
    }
  }

  // SECTION 3: Custom messages from product tags
  // Supports two syntaxes:
  // - _info:message text → renders in a box
  // - _text:message text → renders as plain text
  if (settings.showCustomLabels) {
    for (const rawTag of productTags) {
      const tag = String(rawTag).trim()
      if (!tag) continue

      if (tag.includes('_info:')) {
        const text = tag.split('_info:')[1]
        messages.push(`<div class="save-message save-message--box">${text}</div>`)
      } else if (tag.includes('_text:')) {
        const text = tag.split('_text:')[1]
        messages.push(`<div class="save-message">${text}</div>`)
      }
    }
  }

  // SECTION 4: Final sale message
  if (settings.showFinalSaleMessage) {
    for (const rawTag of productTags) {
      const tag = String(rawTag).trim()
      if (!tag) continue

      if (tag.includes('final-sale')) {
        messages.push(
          `<div class="save-message save-message--final-sale">${settings.finalSaleMessage}</div>`,
        )
        break
      }
    }
  }

  // SECTION 5: Exclusion message
  // Shown when product is tagged for exclusion from global Save Message
  if (settings.showExclusionMessage && excludeFromSave) {
    for (const rawTag of productTags) {
      const tag = String(rawTag).trim()
      if (!tag) continue

      if (tag.includes(settings.exclusionMessageTag)) {
        messages.push(`<div class="save-message">${settings.exclusionMessage}</div>`)
        break
      }
    }
  }

  // SECTION 6: Bundle discount message
  if (settings.bundleProductDiscountMessage) {
    for (const rawTag of productTags) {
      const tag = String(rawTag).trim()
      if (!tag) continue

      if (tag.includes('bundle')) {
        messages.push(
          `<div class="save-message save-message--bundle">${settings.bundleProductDiscountMessage}</div>`,
        )
        break
      }
    }
  }

  // Return all messages concatenated as a single HTML string
  return messages.join('')
}